import { Link } from 'react-router-dom'
import { useInventoryDashboard, useProductsDashboard } from '@/hooks/useDashboardData'
import { useCapacitySummary } from '@/hooks/useStorage'
import { previousTimeWindow } from '@/lib/dashboardLayouts'
import { deltaOf, previousPeriodLabel } from '@/lib/dashboardWidgetFormat'
import { windowLabel } from '@/lib/reportRange'
import { cn } from '@/lib/utils'
import type { LabelCount } from '@/api/dashboardApi'
import type { WidgetBodyProps } from '../builtWidgets'
import { DeltaBadge, Sparkline, STATUS_COLORS } from '../charts'
import { CATEGORICAL, PartsView, SeriesView, dailyRows, partsKind, seriesKind, type Part } from '../views'
import { WidgetQueryState, WidgetStat } from '../WidgetCard'

// ─── Inventory, Storage, Products & Shipping widgets (z8nrz7eqyq) ────────────
//
// Inventory and products read the dashboard endpoints (which reuse reports
// 2.1–2.3); storage reads the Storage page's own capacity summary, so a widget
// and that page can never disagree about how full a freezer is.

const counts = (rows: LabelCount[], color?: string): Part[] =>
  rows.map((r, i) => ({ label: r.label, value: r.count, color: color ?? CATEGORICAL[i % CATEGORICAL.length] }))

const n2 = (x: number) => (Number.isInteger(x) ? x.toLocaleString() : x.toLocaleString(undefined, { maximumFractionDigits: 2 }))

const SHELF_COLORS: Record<string, string> = {
  Expired: STATUS_COLORS.bad,
  'Within 30 days': '#f59e0b',
  '31–90 days': '#facc15',
  'Over 90 days': STATUS_COLORS.good,
  'No expiry date': STATUS_COLORS.muted,
}

// ─── Inventory ───────────────────────────────────────────────────────────────

export function BelowMinimumWidget({ time, view }: WidgetBodyProps) {
  const q = useInventoryDashboard(time.query)
  const parts: Part[] = q.data
    ? [
        { label: 'Out of stock', value: q.data.stock.out, color: STATUS_COLORS.bad },
        { label: 'Below minimum', value: q.data.stock.below, color: '#f59e0b' },
        { label: 'Healthy', value: q.data.stock.ok, color: STATUS_COLORS.good },
      ]
    : []
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.stock.out + q.data.stock.below}
            tone={q.data.stock.out > 0 ? 'red' : q.data.stock.below > 0 ? 'amber' : 'blue'}
            href='/inventory'
            caption='Materials out of stock or below minimum'
            footer={`${q.data.stock.out} out of stock · ${q.data.stock.below} below minimum${
              q.data.stock.noMinimum > 0 ? ` · ${q.data.stock.noMinimum} with no minimum set` : ''
            }`}
          />
        ) : (
          <PartsView view={partsKind(view)} parts={parts} totalLabel='materials' />
        ))}
    </WidgetQueryState>
  )
}

export function StockByCategoryWidget({ time, view }: WidgetBodyProps) {
  const q = useInventoryDashboard(time.query)
  const rows = q.data?.stock.byCategory ?? []
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={rows.length === 0}
      emptyText='No materials with stock levels yet.'
      onRetry={() => void q.refetch()}
    >
      {view === 'table' ? (
        <table className='w-full text-xs'>
          <thead>
            <tr className='border-b border-gray-100 text-left text-[11px] text-gray-400'>
              <th className='py-1 font-medium'>Category</th>
              <th className='py-1 text-right font-medium'>Out</th>
              <th className='py-1 text-right font-medium'>Below</th>
              <th className='py-1 text-right font-medium'>Healthy</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.category} className='border-b border-gray-50 last:border-0'>
                <td className='py-1.5 text-gray-700'>{r.category}</td>
                <td className='py-1.5 text-right font-semibold tabular-nums text-red-600'>{r.out}</td>
                <td className='py-1.5 text-right font-semibold tabular-nums text-amber-600'>{r.below}</td>
                <td className='py-1.5 text-right tabular-nums text-gray-700'>{r.ok}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className='space-y-2.5'>
          {rows.map(r => {
            const total = Math.max(1, r.out + r.below + r.ok)
            return (
              <li key={r.category}>
                <div className='mb-1 flex justify-between text-xs'>
                  <span className='font-medium text-gray-700'>{r.category}</span>
                  <span className='tabular-nums text-gray-500'>
                    {r.out + r.below} of {r.out + r.below + r.ok} need stock
                  </span>
                </div>
                <div className='flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-gray-100'>
                  {[
                    [r.out, STATUS_COLORS.bad, 'Out of stock'],
                    [r.below, '#f59e0b', 'Below minimum'],
                    [r.ok, STATUS_COLORS.good, 'Healthy'],
                  ]
                    .filter(([v]) => Number(v) > 0)
                    .map(([v, c, l]) => (
                      <div key={String(l)} title={`${l}: ${v}`} style={{ width: `${(Number(v) / total) * 100}%`, backgroundColor: String(c) }} />
                    ))}
                </div>
              </li>
            )
          })}
          <li className='flex gap-4 pt-1 text-[11px] text-gray-500'>
            {[
              ['Out of stock', STATUS_COLORS.bad],
              ['Below minimum', '#f59e0b'],
              ['Healthy', STATUS_COLORS.good],
            ].map(([l, c]) => (
              <span key={l} className='flex items-center gap-1.5'>
                <span className='h-2 w-2 rounded-sm' style={{ backgroundColor: c }} aria-hidden />
                {l}
              </span>
            ))}
          </li>
        </ul>
      )}
    </WidgetQueryState>
  )
}

export function ShelfLifeWidget({ time, view }: WidgetBodyProps) {
  const q = useInventoryDashboard(time.query)
  const parts: Part[] = (q.data?.shelfLife ?? []).map(b => ({ label: b.label, value: b.count, color: SHELF_COLORS[b.label] }))
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && q.data.lotsInStock === 0}
      emptyText='No lots in stock.'
      onRetry={() => void q.refetch()}
    >
      {q.data && (
        <div className='flex flex-1 flex-col gap-2'>
          <p className='text-xs text-gray-500'>
            <span className='font-semibold text-red-600'>{q.data.expiredLots} expired</span> ·{' '}
            <span className='font-semibold text-amber-600'>{q.data.expiring30Lots} within 30 days</span> · of {q.data.lotsInStock} lots in stock
          </p>
          <PartsView view={partsKind(view)} parts={parts} totalLabel='lots' />
        </div>
      )}
    </WidgetQueryState>
  )
}

export function ConsumptionWidget({ time, view }: WidgetBodyProps) {
  const q = useInventoryDashboard(time.query)
  const prev = useInventoryDashboard(previousTimeWindow(time).query)
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data && (
        <div className='flex flex-1 flex-col gap-1'>
          <p className='flex flex-wrap items-center gap-2 text-xs text-gray-500'>
            <span className='font-semibold text-gray-900'>{n2(q.data.consumption.totalQty)}</span> units consumed ·{' '}
            {windowLabel(time.window)}
            {prev.data && (
              <DeltaBadge
                delta={deltaOf(q.data.consumption.totalQty, prev.data.consumption.totalQty, null)}
                period={previousPeriodLabel(time.id)}
              />
            )}
          </p>
          <SeriesView
            view={seriesKind(view, 'trend')}
            rows={dailyRows(q.data.consumption.byDay)}
            series={[{ key: 'count', label: 'Consumed', color: STATUS_COLORS.accent }]}
          />
        </div>
      )}
    </WidgetQueryState>
  )
}

export function TopConsumedWidget({ time, view }: WidgetBodyProps) {
  const q = useInventoryDashboard(time.query)
  const parts = counts(q.data?.consumption.topMaterials ?? [], view === 'bars' ? STATUS_COLORS.accent : undefined)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={parts.length === 0}
      emptyText='Nothing was consumed in this period.'
      onRetry={() => void q.refetch()}
    >
      <p className='mb-1 text-[11px] text-gray-400'>Units consumed · {windowLabel(time.window)}</p>
      <PartsView view={partsKind(view)} parts={parts} totalLabel='units' />
    </WidgetQueryState>
  )
}

export function LotsOnHandWidget({ time, view }: WidgetBodyProps) {
  const q = useInventoryDashboard(time.query)
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.lotsInStock}
            href='/inventory'
            caption={`Lots in stock · ${n2(q.data.onHandQty)} units on hand`}
            trend={<Sparkline points={q.data.receivedByDay} label='lots received' />}
            footer={`${q.data.receivedByDay.reduce((n, d) => n + d.count, 0)} lots received · ${windowLabel(time.window)}`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{q.data.receivedByDay.reduce((n, d) => n + d.count, 0)}</span> lots received
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(q.data.receivedByDay)}
              series={[{ key: 'count', label: 'Lots received', color: STATUS_COLORS.accent }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function PendingInventoryWidget({ time, view }: WidgetBodyProps) {
  const q = useInventoryDashboard(time.query)
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view !== 'number' ? (
          <PartsView
            view={partsKind(view, 'donut')}
            totalLabel='lots'
            parts={[
              { label: 'Pending release', value: q.data.pendingLots, color: '#f59e0b' },
              { label: 'Released', value: Math.max(0, q.data.lotsInStock - q.data.pendingLots), color: STATUS_COLORS.good },
            ]}
          />
        ) : (
          <WidgetStat
            value={q.data.pendingLots}
            tone={q.data.pendingLots > 0 ? 'amber' : 'blue'}
            href='/inventory?tab=pending'
            caption='Lots held in quarantine or release'
            footer={`of ${q.data.lotsInStock} lots in stock`}
          />
        ))}
    </WidgetQueryState>
  )
}

// ─── Storage ─────────────────────────────────────────────────────────────────

const FAMILY_LABELS: Record<string, string> = {
  CRYOGENIC: 'Cryogenic (LN2)',
  ULTRA_LOW: 'Ultra-low (−80 °C)',
  FREEZER: 'Freezer (−20 / −40 °C)',
  REFRIGERATED: 'Refrigerated (2–8 °C)',
  AMBIENT_CRT: 'Room temperature',
}
const familyLabel = (f: string | null) => (f ? (FAMILY_LABELS[f] ?? f.replace(/_/g, ' ')) : 'No condition set')

function useStorage() {
  const q = useCapacitySummary()
  const units = (q.data ?? []).filter(u => !u.unavailable)
  const spaces = units.reduce((n, u) => n + u.spaces, 0)
  const used = units.reduce((n, u) => n + u.used, 0)
  return { q, units, spaces, used, pct: spaces > 0 ? Math.round((used / spaces) * 100) : 0 }
}

/** Fill colour by how full: the meter turns amber at 80 % and red at 95 %. */
const fillColor = (pct: number) => (pct >= 95 ? STATUS_COLORS.bad : pct >= 80 ? '#f59e0b' : STATUS_COLORS.accent)

export function StorageCapacityWidget({ view }: WidgetBodyProps) {
  const { q, spaces, used, pct, units } = useStorage()
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && spaces === 0}
      emptyText='No storage units with positions yet.'
      onRetry={() => void q.refetch()}
    >
      {view !== 'number' ? (
        <PartsView
          view={partsKind(view, 'donut')}
          totalLabel='positions'
          parts={[
            { label: 'Used', value: used, color: fillColor(pct) },
            { label: 'Free', value: Math.max(0, spaces - used), color: STATUS_COLORS.accentLighter },
          ]}
        />
      ) : (
        <WidgetStat
          value={`${pct}%`}
          tone={pct >= 95 ? 'red' : pct >= 80 ? 'amber' : 'blue'}
          href='/inventory?tab=storage'
          caption={`${used.toLocaleString()} of ${spaces.toLocaleString()} positions used`}
          trend={
            <div className='h-2 w-full rounded-full bg-blue-100' title={`${pct}% used`}>
              <div className='h-2 rounded-full' style={{ width: `${pct}%`, backgroundColor: fillColor(pct) }} />
            </div>
          }
          footer={`${units.filter(u => u.full).length} of ${units.length} units full`}
        />
      )}
    </WidgetQueryState>
  )
}

export function CapacityByTemperatureWidget({ view }: WidgetBodyProps) {
  const { q, units } = useStorage()
  const byFamily = new Map<string, { used: number; spaces: number; units: number }>()
  for (const u of units) {
    const key = familyLabel(u.family)
    const row = byFamily.get(key) ?? { used: 0, spaces: 0, units: 0 }
    row.used += u.used
    row.spaces += u.spaces
    row.units += 1
    byFamily.set(key, row)
  }
  const rows = [...byFamily].map(([label, r]) => ({ label, ...r, pct: r.spaces > 0 ? Math.round((r.used / r.spaces) * 100) : 0 }))
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && rows.length === 0}
      emptyText='No storage units yet.'
      onRetry={() => void q.refetch()}
    >
      <CapacityRows rows={rows} view={view} />
    </WidgetQueryState>
  )
}

export function FullestUnitsWidget({ view }: WidgetBodyProps) {
  const { q, units } = useStorage()
  const rows = units
    .filter(u => u.spaces > 0)
    .map(u => ({ label: u.name ?? u.code, used: u.used, spaces: u.spaces, units: 1, pct: Math.round((u.used / u.spaces) * 100) }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 8)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && rows.length === 0}
      emptyText='No storage units with positions yet.'
      onRetry={() => void q.refetch()}
    >
      <CapacityRows rows={rows} view={view} />
    </WidgetQueryState>
  )
}

function CapacityRows({
  rows,
  view,
}: {
  rows: { label: string; used: number; spaces: number; units: number; pct: number }[]
  view: string
}) {
  if (view === 'table') {
    return (
      <table className='w-full text-xs'>
        <thead>
          <tr className='border-b border-gray-100 text-left text-[11px] text-gray-400'>
            <th className='py-1 font-medium'>Storage</th>
            <th className='py-1 text-right font-medium'>Used</th>
            <th className='py-1 text-right font-medium'>Free</th>
            <th className='py-1 text-right font-medium'>Full</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.label} className='border-b border-gray-50 last:border-0'>
              <td className='py-1.5 text-gray-700'>{r.label}</td>
              <td className='py-1.5 text-right tabular-nums text-gray-900'>{r.used.toLocaleString()}</td>
              <td className='py-1.5 text-right tabular-nums text-gray-500'>{Math.max(0, r.spaces - r.used).toLocaleString()}</td>
              <td className='py-1.5 text-right font-semibold tabular-nums text-gray-900'>{r.pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }
  return (
    <ul className='space-y-2.5'>
      {rows.map(r => (
        <li key={r.label}>
          <div className='mb-1 flex justify-between text-xs'>
            <span className='truncate font-medium text-gray-700'>{r.label}</span>
            <span className='shrink-0 tabular-nums text-gray-500'>
              {r.used.toLocaleString()} / {r.spaces.toLocaleString()} · <span className='font-semibold text-gray-900'>{r.pct}%</span>
            </span>
          </div>
          <div className='h-2 rounded-full bg-gray-100'>
            <div className='h-2 rounded-full' style={{ width: `${r.pct}%`, backgroundColor: fillColor(r.pct) }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

// ─── Products & Shipping ─────────────────────────────────────────────────────

export function WhereProductsAreWidget({ time, view }: WidgetBodyProps) {
  const q = useProductsDashboard(time.query)
  const parts: Part[] = q.data
    ? [
        { label: 'In storage', value: q.data.state.inStorage, color: STATUS_COLORS.accent },
        { label: 'Shipped', value: q.data.state.shipped, color: STATUS_COLORS.good },
        { label: 'Not yet placed', value: q.data.state.unplaced, color: '#f59e0b' },
      ]
    : []
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && q.data.state.total === 0}
      emptyText='No products yet.'
      onRetry={() => void q.refetch()}
    >
      <PartsView view={partsKind(view, 'donut')} parts={parts} totalLabel='products' />
    </WidgetQueryState>
  )
}

export function ProductsMadeWidget({ time, view }: WidgetBodyProps) {
  const q = useProductsDashboard(time.query)
  const prev = useProductsDashboard(previousTimeWindow(time).query)
  const delta = q.data && prev.data && (
    <DeltaBadge delta={deltaOf(q.data.createdInPeriod, prev.data.createdInPeriod)} period={previousPeriodLabel(time.id)} />
  )
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.createdInPeriod}
            href='/products'
            caption={`Products made · ${windowLabel(time.window)}`}
            delta={delta}
            trend={<Sparkline points={q.data.createdByDay} label='products' />}
            footer={`${q.data.state.total} products in total`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='flex items-center gap-2 text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{q.data.createdInPeriod}</span> made {delta}
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(q.data.createdByDay)}
              series={[{ key: 'count', label: 'Products made', color: STATUS_COLORS.accent }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function ProductsByTypeWidget({ time, view }: WidgetBodyProps) {
  const q = useProductsDashboard(time.query)
  const parts = counts(q.data?.byCatalog ?? [], view === 'bars' ? STATUS_COLORS.accent : undefined)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={parts.length === 0}
      emptyText='No products made in this period.'
      onRetry={() => void q.refetch()}
    >
      <PartsView view={partsKind(view)} parts={parts} totalLabel='products' />
    </WidgetQueryState>
  )
}

export function ShipmentsWidget({ time, view }: WidgetBodyProps) {
  const q = useProductsDashboard(time.query)
  const prev = useProductsDashboard(previousTimeWindow(time).query)
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.shipments}
            href='/products'
            caption={`Shipments sent · ${windowLabel(time.window)}`}
            delta={
              prev.data && <DeltaBadge delta={deltaOf(q.data.shipments, prev.data.shipments, null)} period={previousPeriodLabel(time.id)} />
            }
            trend={<Sparkline points={q.data.shippedByDay} label='products shipped' color={STATUS_COLORS.good} />}
            footer={`${q.data.shippedProducts} products shipped`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{q.data.shipments}</span> shipments ·{' '}
              <span className='font-semibold text-gray-900'>{q.data.shippedProducts}</span> products shipped
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(q.data.shippedByDay)}
              series={[{ key: 'count', label: 'Products shipped', color: STATUS_COLORS.good }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function ShipmentsByPartnerWidget({ time, view }: WidgetBodyProps) {
  const q = useProductsDashboard(time.query)
  const partners = counts(q.data?.byPartner ?? [], view === 'bars' ? STATUS_COLORS.good : undefined)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={partners.length === 0}
      emptyText='Nothing shipped in this period.'
      onRetry={() => void q.refetch()}
    >
      <div className='flex flex-1 flex-col gap-2'>
        <PartsView view={partsKind(view)} parts={partners} totalLabel='shipments' />
        {q.data && q.data.byCourier.length > 0 && (
          <p className={cn('text-[11px] text-gray-400')}>
            Couriers: {q.data.byCourier.map(c => `${c.label} ${c.count}`).join(' · ')}
          </p>
        )}
        <Link to='/products' className='sr-only'>
          Open Products
        </Link>
      </div>
    </WidgetQueryState>
  )
}
